// Unified Task Hooks
// Uses /api/v1/tasks endpoints (Firestore-based)
// Shared by both project-based and personnel-based (teamwork) views

import { useQuery, useMutation, useQueryClient } from 'react-query';
import * as taskService from '@/services/taskService';
import {
  ITask,
  ITaskCreateRequest,
  ITaskUpdateRequest,
  ITaskMoveRequest,
  ITaskOrder,
  ITaskListResponse,
  ITaskByOwnerResponse,
  ITaskByProjectResponse,
  ITaskByPhaseResponse,
  ITaskStats,
  IDependencyCheck,
  ITaskFilter,
} from '@/types/task';

// ============================================================================
// Query Keys
// ============================================================================

export const TASK_QUERY_KEYS = {
  all: ['tasks'] as const,
  list: () => [...TASK_QUERY_KEYS.all, 'list'] as const,
  listWithFilter: (filter?: ITaskFilter) =>
    [...TASK_QUERY_KEYS.list(), filter] as const,
  detail: (id: string) => [...TASK_QUERY_KEYS.all, 'detail', id] as const,
  stats: (filter?: { project_id?: string; task_owner?: string }) =>
    [...TASK_QUERY_KEYS.all, 'stats', filter] as const,
  byOwner: (owner: string, includeCompleted?: boolean) =>
    [...TASK_QUERY_KEYS.all, 'by-owner', owner, includeCompleted] as const,
  byProject: (projectId: string) =>
    [...TASK_QUERY_KEYS.all, 'by-project', projectId] as const,
  byPhase: (projectId: string, phaseId: string) =>
    [...TASK_QUERY_KEYS.all, 'by-phase', projectId, phaseId] as const,
  dependencies: (id: string) =>
    [...TASK_QUERY_KEYS.all, 'dependencies', id] as const,
};

// ============================================================================
// Core CRUD Hooks
// ============================================================================

export const useTaskList = (filter?: ITaskFilter) => {
  return useQuery<ITaskListResponse, Error>(
    TASK_QUERY_KEYS.listWithFilter(filter),
    () => taskService.listTasks(filter),
    {
      staleTime: 30000,
      cacheTime: 300000,
      refetchInterval: 60000,
    }
  );
};

export const useTask = (id: string) => {
  return useQuery<ITask, Error>(
    TASK_QUERY_KEYS.detail(id),
    () => taskService.getTask(id),
    {
      enabled: !!id,
      staleTime: 30000,
    }
  );
};

export const useCreateTask = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: ITaskCreateRequest) => taskService.createTask(data),
    {
      onSuccess: (task) => {
        // Invalidate all task queries since we don't know which views need updating
        queryClient.invalidateQueries(TASK_QUERY_KEYS.all);
        // Also invalidate project-specific queries if task has project_id
        if (task.project_id) {
          queryClient.invalidateQueries(['projects', 'detail', task.project_id]);
        }
      },
    }
  );
};

export const useUpdateTask = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, data }: { id: string; data: ITaskUpdateRequest }) =>
      taskService.updateTask(id, data),
    {
      onSuccess: (task) => {
        queryClient.invalidateQueries(TASK_QUERY_KEYS.all);
        if (task.project_id) {
          queryClient.invalidateQueries(['projects', 'detail', task.project_id]);
        }
      },
    }
  );
};

export const useDeleteTask = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (id: string) => taskService.deleteTask(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(TASK_QUERY_KEYS.all);
      },
    }
  );
};

// ============================================================================
// Task Operation Hooks
// ============================================================================

export const useMoveTask = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, data }: { id: string; data: ITaskMoveRequest }) =>
      taskService.moveTask(id, data),
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries(TASK_QUERY_KEYS.all);
        if (response.task.project_id) {
          queryClient.invalidateQueries([
            'projects',
            'detail',
            response.task.project_id,
          ]);
        }
      },
    }
  );
};

export const useReorderTasks = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (orders: ITaskOrder[]) => taskService.reorderTasks(orders),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(TASK_QUERY_KEYS.all);
      },
    }
  );
};

export const useCheckDependencies = (id: string, enabled = true) => {
  return useQuery<IDependencyCheck, Error>(
    TASK_QUERY_KEYS.dependencies(id),
    () => taskService.checkDependencies(id),
    {
      enabled: !!id && enabled,
      staleTime: 10000, // More frequent updates for dependency checks
    }
  );
};

// ============================================================================
// View-specific Hooks
// ============================================================================

export const useTaskStats = (filter?: { project_id?: string; task_owner?: string }) => {
  return useQuery<ITaskStats, Error>(
    TASK_QUERY_KEYS.stats(filter),
    () => taskService.getTaskStats(filter),
    {
      staleTime: 30000,
    }
  );
};

export const useTasksByOwner = (owner: string, includeCompleted = true) => {
  return useQuery<ITaskByOwnerResponse, Error>(
    TASK_QUERY_KEYS.byOwner(owner, includeCompleted),
    () => taskService.listTasksByOwner(owner, includeCompleted),
    {
      enabled: !!owner,
      staleTime: 30000,
      refetchInterval: 60000,
    }
  );
};

export const useTasksByProject = (projectId: string) => {
  return useQuery<ITaskByProjectResponse, Error>(
    TASK_QUERY_KEYS.byProject(projectId),
    () => taskService.listTasksByProject(projectId),
    {
      enabled: !!projectId,
      staleTime: 30000,
      refetchInterval: 60000,
    }
  );
};

export const useTasksByPhase = (projectId: string, phaseId: string) => {
  return useQuery<ITaskByPhaseResponse, Error>(
    TASK_QUERY_KEYS.byPhase(projectId, phaseId),
    () => taskService.listTasksByPhase(projectId, phaseId),
    {
      enabled: !!projectId && !!phaseId,
      staleTime: 30000,
      refetchInterval: 60000,
    }
  );
};

// ============================================================================
// Re-export helper functions
// ============================================================================

export { groupTasksByStatus, groupTasksByPhase, calculatePhaseProgress } from '@/services/taskService';
